/**
 * <mega-menu-preview>
 *
 * Swaps the promo image in the "Shop" mega menu to a preview of the category
 * the shopper is pointing at. Each category link carries a
 * `data-mega-preview-key`; the matching image carries the same
 * `data-mega-preview-img`.
 *
 * The active image only changes on `mouseenter` / `focus` of a link — it is
 * never cleared on leave. That way the last previewed image stays put while the
 * cursor travels across the gaps between links, so nothing flickers. During a
 * swap the outgoing image is kept opaque underneath (`.is-prev`) until the
 * incoming one (`.is-active`) has faded in on top, so the static promo never
 * shows through.
 */
class MegaMenuPreview extends HTMLElement {
  connectedCallback() {
    this.images = this.querySelectorAll('[data-mega-preview-img]');
    if (this.images.length === 0) return;

    // The first preview is marked active in the markup so it shows even
    // before this script runs.
    this.current = this.querySelector('[data-mega-preview-img].is-active') || this.images[0];
    this.current.classList.add('is-active');

    this.querySelectorAll('[data-mega-preview-key]').forEach((link) => {
      const image = this.querySelector(
        `[data-mega-preview-img="${CSS.escape(link.dataset.megaPreviewKey)}"]`
      );
      if (!image) return;

      const activate = this.setActive.bind(this, image);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });
  }

  setActive(image) {
    if (!image || image === this.current) return;

    const previous = this.current;
    previous.classList.remove('is-active');
    previous.classList.add('is-prev');

    image.classList.remove('is-prev');
    image.classList.add('is-active');
    this.current = image;

    // Drop the outgoing image once the incoming one has fully covered it,
    // unless it has meanwhile become the active image again.
    window.setTimeout(() => {
      if (previous !== this.current) previous.classList.remove('is-prev');
    }, 320);
  }
}

customElements.define('mega-menu-preview', MegaMenuPreview);
